import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, LogIn } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_jci-luxury-portal/artifacts/914ad0rv_Artboard%201.png";

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  { path: '/membership', label: 'Membership' },
  { path: '/amenities', label: 'Amenities' },
  { path: '/events', label: 'Events' },
  { path: '/gallery', label: 'Gallery' },
  { path: '/csr', label: 'CSR & Impact' },
  { path: '/contact', label: 'Contact' },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [settings, setSettings] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleLogin = () => {
    if (settings?.external_login_url) {
      window.open(settings.external_login_url, '_blank');
    }
  };

  const logoUrl = settings?.logo_url || DEFAULT_LOGO;

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'nav-glass py-3' : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
              <img
                src={logoUrl}
                alt="Club JCI Ltd"
                className="h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`text-sm tracking-wide transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-[#D4AF37]'
                      : 'text-[#F1F5F9] hover:text-[#D4AF37]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Login Button & Mobile Menu Toggle */}
            <div className="flex items-center gap-4">
              {settings?.show_member_login !== false && (
                <button
                  onClick={handleLogin}
                  data-testid="member-login-btn"
                  className="hidden sm:flex items-center gap-2 btn-gold text-xs py-2 px-4 rounded-sm"
                >
                  <LogIn size={14} />
                  Member Login
                </button>
              )}
              
              <button
                onClick={() => setIsOpen(!isOpen)}
                data-testid="mobile-menu-toggle"
                className="lg:hidden text-[#F1F5F9] p-2"
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-[#051025] flex flex-col items-center justify-center gap-6"
            data-testid="mobile-menu"
          >
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-5 right-5 text-[#F1F5F9]"
            >
              <X size={24} />
            </button>
            
            <img
              src={logoUrl}
              alt="Club JCI Ltd"
              className="h-16 w-auto mb-8"
            />
            
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`text-xl tracking-wide transition-colors duration-300 ${
                  location.pathname === link.path
                    ? 'text-[#D4AF37]'
                    : 'text-[#F1F5F9] hover:text-[#D4AF37]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {settings?.show_member_login !== false && (
              <button
                onClick={handleLogin}
                className="mt-8 btn-gold-filled text-sm py-3 px-6 rounded-sm flex items-center gap-2"
              >
                <LogIn size={16} />
                Member Login
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
